import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as Ably from "ably";
import { useSelector } from "react-redux";
import { buildAssistantHeaders, buildAssistantUrl } from "../utils/assistantApi";
import { getOrCreateSession } from "../utils/SessionManager";
import { ASSISTANT_SESSION_KEY } from "../utils/assistantStore";
import { consumeHandoffName, consumeHandoffParam } from "../utils/assistantHandoff";
import { AI_ASSISTANT_CONSTANTS } from "../constants/aiAssistantConstants";

const DEFAULT_CONTEXT = {
  ablyClient: null,
  clientId: null,
  conversationId: null,
  isSessionLoading: false,
  isSourceConversationSession: false,
  sessionInitError: null,
  realtimeConnectionState: "idle",
};

const AIAssistantAblyContext = createContext(DEFAULT_CONTEXT);

const getSourceConversationId = () => consumeHandoffName() || consumeHandoffParam();
const resolveSessionInitError = (value) => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (value && typeof value === "object") {
    if (typeof value.message === "string" && value.message.trim()) {
      return value.message.trim();
    }
    if (typeof value.detail === "string" && value.detail.trim()) {
      return value.detail.trim();
    }
  }
  return AI_ASSISTANT_CONSTANTS.ERRORS.SESSION_UNAVAILABLE;
};

export const AIAssistantAblyProvider = ({ children }) => {
  const userDetail = useSelector((state) => state.userDetail);
  const isChatOpen = useSelector((state) => state?.chat?.isChatOpen ?? true);
  const [identity, setIdentity] = useState({ clientId: null, conversationId: null });
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isSourceConversationSession, setIsSourceConversationSession] = useState(false);
  const [sessionInitError, setSessionInitError] = useState(null);
  const [realtimeConnectionState, setRealtimeConnectionState] = useState("idle");
  const heartbeatRef = React.useRef(null);
  const lastSessionUserIdRef = React.useRef(undefined);

  useEffect(() => {
    let isMounted = true;
    const loadSession = async () => {
      if (!isMounted) return;
      setIsSessionLoading(true);
      const sourceConversationId = getSourceConversationId();
      setSessionInitError(null);
      setIsSourceConversationSession(Boolean(sourceConversationId));
      try {
        const session = await getOrCreateSession({
          userId: userDetail?.id,
          sourceConversationId,
        });
        if (!isMounted) return;
        console.debug("[AI Assistant] initial session resolved", {
          sourceConversationId,
          session,
        });
        const hasSourceConversation = Boolean(session?.sourceConversationId);
        setIsSourceConversationSession(hasSourceConversation);
        if (session?.clientId && session?.conversationId) {
          setSessionInitError(null);
          setIdentity({
            clientId: session.clientId,
            conversationId: session.conversationId,
          });
        } else if (session?.success === false) {
          setSessionInitError(resolveSessionInitError(session?.error));
          console.warn("[AI Assistant] Unable to initialize session", session?.error);
        } else {
          setSessionInitError(AI_ASSISTANT_CONSTANTS.ERRORS.SESSION_UNAVAILABLE);
        }
      } catch (error) {
        setIsSourceConversationSession(false);
        setSessionInitError(resolveSessionInitError(error));
        console.error("[AI Assistant] Session initialization failed", error);
      } finally {
        if (isMounted) {
          lastSessionUserIdRef.current = userDetail?.id;
          setIsSessionLoading(false);
        }
      }
    };
    const currentUserId = userDetail?.id;
    if (currentUserId === undefined || !isChatOpen) {
      return () => {
        isMounted = false;
      };
    }
    const userChanged =
      lastSessionUserIdRef.current !== undefined &&
      lastSessionUserIdRef.current !== currentUserId;
    if (userChanged) {
      setIdentity({ clientId: null, conversationId: null });
      setIsSourceConversationSession(false);
      setSessionInitError(null);
      setRealtimeConnectionState("idle");
      try { sessionStorage.removeItem(ASSISTANT_SESSION_KEY); } catch {}
      lastSessionUserIdRef.current = currentUserId;
      return () => {
        isMounted = false;
      };
    }
    if (!identity?.clientId && !identity?.conversationId) {
      loadSession();
    }
    return () => {
      isMounted = false;
    };
  }, [
    userDetail?.id,
    isChatOpen,
    identity?.clientId,
    identity?.conversationId,
  ]);

  useEffect(() => {
    const handleSessionUpdate = (event) => {
      const { clientId, conversationId, meta } = event?.detail || {};
      if (!clientId || !conversationId) return;
      console.debug("[AI Assistant] session update event", {
        clientId,
        conversationId,
        meta,
      });
      if (meta?.source === "cloneSession") {
        setIsSourceConversationSession(true);
      } else if (typeof meta?.source === "string" && meta.source.length > 0) {
        setIsSourceConversationSession(false);
      }
      setSessionInitError(null);
      setIdentity({ clientId, conversationId });
    };
    const handleSessionReset = () => {
      setIdentity({ clientId: null, conversationId: null });
      setIsSessionLoading(false);
      setIsSourceConversationSession(false);
      setSessionInitError(null);
      setRealtimeConnectionState("idle");
    };
    window.addEventListener(
      AI_ASSISTANT_CONSTANTS.EVENTS.SESSION_UPDATED,
      handleSessionUpdate,
    );
    window.addEventListener(
      AI_ASSISTANT_CONSTANTS.EVENTS.IDENTITY_UPDATED,
      handleSessionUpdate,
    );
    window.addEventListener(
      AI_ASSISTANT_CONSTANTS.EVENTS.SESSION_RESET,
      handleSessionReset,
    );
    return () => {
      window.removeEventListener(
        AI_ASSISTANT_CONSTANTS.EVENTS.SESSION_UPDATED,
        handleSessionUpdate,
      );
      window.removeEventListener(
        AI_ASSISTANT_CONSTANTS.EVENTS.IDENTITY_UPDATED,
        handleSessionUpdate,
      );
      window.removeEventListener(
        AI_ASSISTANT_CONSTANTS.EVENTS.SESSION_RESET,
        handleSessionReset,
      );
    };
  }, []);

  const ablyClient = useMemo(() => {
    if (!identity?.clientId) return null;
    const authUrl = `${buildAssistantUrl(
      AI_ASSISTANT_CONSTANTS.ABLY.AUTH_PATH,
    )}?${AI_ASSISTANT_CONSTANTS.ABLY.QUERY_KEYS.CLIENT_ID}=${encodeURIComponent(
      identity.clientId,
    )}&${AI_ASSISTANT_CONSTANTS.ABLY.QUERY_KEYS.RND}=${Date.now()}`;
    const authHeaders = buildAssistantHeaders({
      [AI_ASSISTANT_CONSTANTS.API.HEADERS.CONTENT_TYPE]: undefined,
    });
    return new Ably.Realtime({
      clientId: identity.clientId,
      closeOnUnload: false,
      autoConnect: true,
      authUrl,
      authHeaders,
    });
  }, [identity?.clientId]);

  useEffect(() => {
    if (!ablyClient) {
      setRealtimeConnectionState("idle");
      return undefined;
    }
    const handleConnectionState = (stateChange) => {
      const nextState =
        stateChange?.current || stateChange?.currentState || stateChange;
      if (typeof nextState === "string" && nextState.trim()) {
        setRealtimeConnectionState(nextState);
      }
    };
    const handleConnectionFailed = (stateChange) => {
      console.error("[AI Assistant Ably] Connection failed", stateChange);
    };
    const watchedStates = [
      "initialized",
      "connecting",
      "connected",
      "disconnected",
      "suspended",
      "closing",
      "closed",
      "failed",
    ];
    watchedStates.forEach((state) => {
      ablyClient.connection.on(state, handleConnectionState);
    });
    handleConnectionState({ current: ablyClient.connection.state || "connecting" });
    if (
      typeof ablyClient.connect === "function" &&
      (ablyClient.connection.state === "initialized" ||
        ablyClient.connection.state === "disconnected")
    ) {
      ablyClient.connect();
    }
    ablyClient.connection.on(
      AI_ASSISTANT_CONSTANTS.ABLY.CONNECTION_FAILED_EVENT,
      handleConnectionFailed,
    );
    heartbeatRef.current = setInterval(async () => {
      try {
        await ablyClient.connection.ping();
      } catch (error) {
        console.warn("[AI Assistant Ably] Heartbeat failed", error);
      }
    }, AI_ASSISTANT_CONSTANTS.ABLY.HEARTBEAT_INTERVAL_MS);
    return () => {
      watchedStates.forEach((state) => {
        ablyClient.connection.off(state, handleConnectionState);
      });
      ablyClient.connection.off(
        AI_ASSISTANT_CONSTANTS.ABLY.CONNECTION_FAILED_EVENT,
        handleConnectionFailed,
      );
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      ablyClient.close();
    };
  }, [ablyClient]);

  const contextValue = useMemo(
    () => ({
      ablyClient,
      clientId: identity?.clientId,
      conversationId: identity?.conversationId,
      isSessionLoading,
      isSourceConversationSession,
      sessionInitError,
      realtimeConnectionState,
    }),
    [
      ablyClient,
      identity?.clientId,
      identity?.conversationId,
      isSessionLoading,
      isSourceConversationSession,
      sessionInitError,
      realtimeConnectionState,
    ],
  );

  return (
    <AIAssistantAblyContext.Provider value={contextValue}>
      {children}
    </AIAssistantAblyContext.Provider>
  );
};

export const useAssistantAbly = () =>
  useContext(AIAssistantAblyContext) || DEFAULT_CONTEXT;
